// =====================================================================================
// INVRT Radar — web console — suppression manager
//
// The suppression list is how Radar knows what a customer already owns, which is what
// makes "never charged for a contact you already have" a guarantee rather than a slogan.
// The API's own guidance is on screen: build this list BEFORE prospecting, and removing an
// entry means a future match against it can be billed again.
// =====================================================================================

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { useApiClient } from "@/hooks/useApi";
import { useSession } from "@/auth/SessionProvider";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Select, Textarea } from "@/components/ui/input";
import { useToast } from "@/components/ui/toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { formatDate, pluralise } from "@/lib/format";
import type {
  SuppressionDeleteResponse,
  SuppressionKind,
  SuppressionList,
  SuppressionWriteResponse,
} from "@/lib/types";

const KIND_LABELS: Record<SuppressionKind, string> = {
  email: "Email address",
  domain: "Domain",
  company: "Company name",
};

export function SuppressionPage() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { orgId } = useSession();
  const { push } = useToast();

  const [kindFilter, setKindFilter] = useState<"all" | SuppressionKind>("all");
  const [offset, setOffset] = useState(0);
  const [adding, setAdding] = useState(false);
  const [addKind, setAddKind] = useState<SuppressionKind>("email");
  const [addValues, setAddValues] = useState("");
  const [removing, setRemoving] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["suppression", orgId, kindFilter, offset],
    queryFn: () =>
      api.get<SuppressionList>("/suppression", {
        query: {
          limit: 50,
          offset,
          ...(kindFilter === "all" ? {} : { kind: kindFilter }),
        },
      }),
    enabled: Boolean(orgId),
  });

  const parsedValues = addValues
    .split(/[\n,]/)
    .map((value) => value.trim())
    .filter((value) => value !== "");

  const add = useMutation({
    mutationFn: () =>
      api.post<SuppressionWriteResponse>("/suppression", {
        entries: parsedValues.map((value) => ({ kind: addKind, value })),
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ["suppression", orgId] });
      setAdding(false);
      setAddValues("");
      push({
        tone: "success",
        title: `${pluralise(result.new_entries, "entry", "entries")} added`,
        body: "Already in force for anything you do from now on.",
      });
    },
  });

  const remove = useMutation({
    mutationFn: (entryId: string) =>
      api.delete<SuppressionDeleteResponse>("/suppression", { entry_ids: [entryId] }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["suppression", orgId] });
      setRemoving(null);
      push({
        tone: "warning",
        title: "Entry removed",
        body: "A future match against it can be billed again.",
      });
    },
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">Suppression</h1>
          <p className="mt-1 max-w-2xl text-sm text-ink-muted">
            The people and companies you already have. Radar checks this list before it
            charges you for anything, so build it up before you start prospecting or turn
            a pixel on.
          </p>
        </div>
        <Button onClick={() => setAdding(true)}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          Add entries
        </Button>
      </div>

      <Card>
        <CardHeader className="flex-row flex-wrap items-center justify-between gap-3">
          <CardTitle>
            {list.data ? `${list.data.pagination.total.toLocaleString("en-GB")} entries` : "Entries"}
          </CardTitle>
          <Select
            label="Kind"
            value={kindFilter}
            onChange={(event) => {
              setOffset(0);
              setKindFilter(event.target.value as typeof kindFilter);
            }}
            className="w-48"
          >
            <option value="all">All kinds</option>
            <option value="email">Email addresses</option>
            <option value="domain">Domains</option>
            <option value="company">Company names</option>
          </Select>
        </CardHeader>

        <CardContent>
          {list.isLoading ? <LoadingState label="Loading your list…" /> : null}
          {list.isError ? (
            <ErrorState error={list.error} onRetry={() => void list.refetch()} />
          ) : null}

          {list.isSuccess ? (
            list.data.entries.length === 0 ? (
              <EmptyState
                title="Nothing suppressed yet"
                body="Paste in your existing customers and contacts. Radar will never charge you to reveal or identify anyone on this list."
                action={<Button onClick={() => setAdding(true)}>Add entries</Button>}
              />
            ) : (
              <>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Value</TableHeaderCell>
                      <TableHeaderCell>Kind</TableHeaderCell>
                      <TableHeaderCell>Added</TableHeaderCell>
                      <TableHeaderCell>Pushed upstream</TableHeaderCell>
                      <TableHeaderCell className="w-10">
                        <span className="sr-only">Remove</span>
                      </TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {list.data.entries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell className="font-mono text-xs">{entry.value_norm}</TableCell>
                        <TableCell className="text-ink-muted">
                          {KIND_LABELS[entry.kind]}
                        </TableCell>
                        <TableCell className="text-ink-muted">
                          {formatDate(entry.created_at)}
                        </TableCell>
                        <TableCell>
                          {entry.synced_prospecting && entry.synced_web_id ? (
                            <Badge tone="success">Pushed to both services</Badge>
                          ) : entry.synced_prospecting || entry.synced_web_id ? (
                            <Badge tone="info">
                              {entry.synced_prospecting
                                ? "Pushed to prospecting, website push pending"
                                : "Pushed to website identification, prospecting push pending"}
                            </Badge>
                          ) : (
                            <Badge tone="neutral">In force here, push pending</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Remove ${entry.value_norm}`}
                            onClick={() => setRemoving(entry.id)}
                          >
                            <Trash2 className="h-4 w-4 text-danger" aria-hidden="true" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <div className="flex items-center justify-between pt-3">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - 50))}
                  >
                    Previous
                  </Button>
                  <span className="text-xs text-ink-muted">
                    Showing {offset + 1} to{" "}
                    {offset + list.data.entries.length} of{" "}
                    {list.data.pagination.total.toLocaleString("en-GB")}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={offset + list.data.entries.length >= list.data.pagination.total}
                    onClick={() => setOffset(offset + 50)}
                  >
                    Next
                  </Button>
                </div>
              </>
            )
          ) : null}
        </CardContent>
      </Card>

      <Dialog
        open={adding}
        onClose={() => setAdding(false)}
        title="Add suppression entries"
        description="In force for billing the moment they are saved. The push out to the upstream providers happens in the background."
        footer={
          <>
            <Button variant="secondary" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => add.mutate()}
              loading={add.isPending}
              disabled={parsedValues.length === 0}
            >
              Add {parsedValues.length > 0 ? pluralise(parsedValues.length, "entry", "entries") : ""}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <Select
            label="What kind of entry?"
            value={addKind}
            onChange={(event) => setAddKind(event.target.value as SuppressionKind)}
          >
            <option value="email">Email addresses</option>
            <option value="domain">Domains</option>
            <option value="company">Company names</option>
          </Select>
          <Textarea
            label="Values"
            value={addValues}
            onChange={(event) => setAddValues(event.target.value)}
            rows={8}
            placeholder={"one per line\nor separated by commas"}
            hint="Radar lower-cases and trims each value before storing it."
          />
          {add.isError ? <ErrorState error={add.error} /> : null}
        </div>
      </Dialog>

      <Dialog
        open={removing !== null}
        onClose={() => setRemoving(null)}
        title="Remove this entry?"
        description="Once it is gone, a future match against it can be billed again. Only remove it if you mean that."
        footer={
          <>
            <Button variant="secondary" onClick={() => setRemoving(null)}>
              Keep it
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => removing && remove.mutate(removing)}
            >
              Remove it
            </Button>
          </>
        }
      >
        {remove.isError ? <ErrorState error={remove.error} /> : null}
      </Dialog>
    </div>
  );
}
